import { useContext } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Divider,
  HStack,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuChevronRight, LuPlus } from 'react-icons/lu';

import { Form, FormField, FormFieldError, FormFieldItem } from '@/components';
import { Icon } from '@/components/Icons';
import { SquareIconContainer } from '@/components/Icons/IconContainer';
import {
  DoneMarkIcon,
  IconInfoCircle,
} from '@/components/Icons/icons-generated/';
import { Link } from '@/components/Link';
import { MarkdownEditor } from '@/components/MarkdownEditor/MarkdownEditor';
import { useAddJobDescriptionMutation } from '@/features/admin-interview/useAdminInterviewQueries';

import { AdminNewInterviewContext } from './PageAdminNewInterview.ctx';
import { RecordIntroductionVideoSection } from './RecordIntroductionVideoSection';
import type { NewInterviewFormStepProps } from './common';

type JobForm = {
  jobDescription: string;
  recalculate: boolean;
};
interface FinalizeProps extends NewInterviewFormStepProps {
  onNew: () => void;
}

export const StepFinalizeNewInterview = ({
  onNew,
  isActive,
}: FinalizeProps) => {
  const { t } = useTranslation(['interview', 'candidate', 'common']);
  const toast = useToast();
  const { interviewId, jobDescription } = useContext(AdminNewInterviewContext);

  const form = useForm<JobForm>({
    mode: 'onBlur',
    defaultValues: {
      jobDescription: '',
      recalculate: true,
    },
  });

  const addJobDescriptionMutation = useAddJobDescriptionMutation({
    onSuccess() {
      toast({
        title: t('candidate:inviteNewCandidate.addedJobDescription'),
        status: 'success',
      });
      window.scrollTo(0, 0);

      form.reset({
        jobDescription: form.getValues('jobDescription'),
      });
    },
    onError() {
      toast({
        title: t('candidate:inviteNewCandidate.addCandidateError'),
        status: 'error',
      });
    },
  });

  const onSubmit: SubmitHandler<JobForm> = async (data) => {
    if (!interviewId.current) return;

    await addJobDescriptionMutation.mutateAsync({
      interviewId: interviewId.current,
      body: {
        jobDescription: data.jobDescription,
      },
    });
  };

  if (!isActive) return null;

  return (
    <Center height="90%">
      <VStack spacing={5}>
        <SquareIconContainer
          icon={DoneMarkIcon}
          iconSize={32}
          size={64}
          borderRadius="full"
          title={t('interview:congratulations')}
          titleSize="2xl"
          titlePosition="bottom"
          gap="20px"
        />
        <Text align="center">
          {t('interview:createNewInterview.InterviewConfirmation.description')}
        </Text>
        <ButtonGroup mt={5} gap={5}>
          <Button
            onClick={onNew}
            variant="@secondary"
            leftIcon={<Icon icon={LuPlus} fontSize="lg" />}
          >
            {t('interview:createNewInterview.createNewInterview')}
          </Button>

          <Link to={`/admin/interviews/${interviewId.current}`}>
            <Button
              rightIcon={<Icon icon={LuChevronRight} fontSize="xl" />}
              variant="@primary"
            >
              {t('interview:goToInterviewDetail')}
            </Button>
          </Link>
        </ButtonGroup>

        <Divider />

        <RecordIntroductionVideoSection />

        {!jobDescription.current && (
          <>
            <Form {...form} noHtmlForm>
              <Stack w="full" spacing={4}>
                <Stack
                  p={4}
                  rounded="lg"
                  background="linear-gradient(96.88deg, rgba(191, 140, 255, 0.12) 20.47%, rgba(255, 46, 0, 0.0384) 146.54%);"
                >
                  <Text color="gray.700" fontSize="sm">
                    {t('interview:jobDescription.label')}
                  </Text>
                  <FormField
                    control={form.control}
                    name="jobDescription"
                    type="custom"
                    render={({
                      field: { value, ref, onBlur, onChange, name },
                    }) => {
                      return (
                        <FormFieldItem>
                          <Box
                            borderRadius="lg"
                            border="1px"
                            borderColor="gray.200"
                            background="white"
                            id={name}
                          >
                            <MarkdownEditor
                              placeholder={t(
                                'interview:jobDescription.placeholder'
                              )}
                              markdown={value ?? ''}
                              onBlur={onBlur}
                              ref={ref}
                              onChange={onChange}
                            />
                          </Box>
                          <FormFieldError />
                        </FormFieldItem>
                      );
                    }}
                  />
                  <HStack spacing={1} color="gray.500">
                    <IconInfoCircle />
                    <Text fontSize="sm">
                      {t('interview:jobDescription.helperText')}
                    </Text>
                  </HStack>
                  <Button
                    isLoading={addJobDescriptionMutation.isPending}
                    isDisabled={!form.formState.isDirty}
                    rightIcon={<Icon icon={LuChevronRight} fontSize="xl" />}
                    variant="@primary"
                    w="fit-content"
                    size="sm"
                    alignSelf="flex-end"
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {t('common:actions.save')}
                  </Button>
                </Stack>
              </Stack>
            </Form>
          </>
        )}
      </VStack>
    </Center>
  );
};
